import React, { useState, useEffect, useRef } from 'react';
import {
  ComposedChart,
  LineChart,
  AreaChart,
  BarChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { formatCurrency } from '../utils/formatters';
import { closePosition, executeOrder } from '../services/trading';
import { API_BASE_URL } from '../config';
import socket from '../services/socket';
// Custom Candlestick component for Recharts
const Candlestick = ({ payload, x, width, background, priceMin, priceMax }) => {
  if (!payload || payload.open == null || payload.high == null || payload.low == null || payload.close == null) {
    return null;
  }
  if (!background) return null;

  const { open, high, low, close } = payload;
  const isGreen = close >= open;
  const color = isGreen ? '#00ff88' : '#ff4444';
  const candleWidth = Math.max(width * 0.6, 2);
  const candleX = x + (width - candleWidth) / 2;

  // Use the background (chart area) dimensions from Recharts
  const chartTop = background.y;
  const chartHeight = background.height;
  const priceRange = priceMax - priceMin || 1;

  // Scale values to the chart's coordinate system (Y is inverted - higher values = lower Y)
  const getY = (value) => {
    return chartTop + chartHeight - ((value - priceMin) / priceRange) * chartHeight;
  };

  const openY = getY(open);
  const closeY = getY(close);
  const highY = getY(high);
  const lowY = getY(low);

  const bodyTop = Math.min(openY, closeY);
  const bodyHeight = Math.max(Math.abs(closeY - openY), 1);

  return (
    <g>
      {/* High-Low line (wick) */}
      <line
        x1={candleX + candleWidth / 2}
        y1={highY}
        x2={candleX + candleWidth / 2}
        y2={lowY}
        stroke={color}
        strokeWidth={1}
      />

      {/* Body rectangle */}
      <rect
        x={candleX}
        y={bodyTop}
        width={candleWidth}
        height={bodyHeight}
        fill={isGreen ? color : 'transparent'}
        stroke={color}
        strokeWidth={1}
      />
    </g>
  );
};

// Factory to create CandlestickShape with price range info
const createCandlestickShape = (priceMin, priceMax) => {
  return (props) => {
    const { payload, x, width, background } = props;
    return (
      <Candlestick
        payload={payload}
        x={x}
        width={width}
        background={background}
        priceMin={priceMin}
        priceMax={priceMax}
      />
    );
  };
};

const CandlestickChart = ({
  stockId,
  ticker,
  positions = [],
  isProMode,
  onPositionUpdate,
  userId,
  canTrade = true
}) => {
  const [chartData, setChartData] = useState([]);
  const [timeRange, setTimeRange] = useState('30m'); // Default to trading block duration
  const [chartType, setChartType] = useState(() => {
    // Persist chart type preference in sessionStorage
    return sessionStorage.getItem(`chartType-${stockId}`) || 'candlestick';
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [orderAmount, setOrderAmount] = useState('1');
  const [stockInfo, setStockInfo] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingTrade, setPendingTrade] = useState(null);
  const [showVolume, setShowVolume] = useState(true);
  const [priceScale, setPriceScale] = useState('auto'); // 'auto' or 'log'
  const [userBalance, setUserBalance] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Available intervals for each time range - optimized for 45-min trading blocks
  // All short timeframes use 1m intervals for granular price movement
  const intervalMapping = {
    '1m': '1m',    // Single minute view
    '5m': '1m',
    '15m': '1m',
    '30m': '1m',
    '1h': '1m',    // 60 candles at 1m each
    '4h': '5m',
    '1d': '15m'
  };

  // Fetch candlestick data from the enhanced API
  // isRefresh=true means don't show loading spinner (prevents flicker on auto-refresh)
  const fetchChartData = async (range = timeRange, isRefresh = false) => {
    if (!stockId) return;

    if (!isRefresh) setLoading(true);
    setError(null);

    try {
      const interval = intervalMapping[range] || '1m';
      // Calculate limit based on time range (all using 1m intervals for short ranges)
      const limitMapping = {
        '1m': 1,     // Last minute only
        '5m': 5,
        '15m': 15,
        '30m': 30,
        '1h': 60,
        '4h': 48,    // 4h at 5m intervals
        '1d': 96     // 1d at 15m intervals
      };
      const limit = limitMapping[range] || 60;

      const response = await fetch(
        `${API_BASE_URL}/api/stocks/${stockId}/history?interval=${interval}&limit=${limit}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch chart data: ${response.statusText}`);
      }

      const data = await response.json();

      // Format data for the chart
      const formattedData = data.data.map((candle, index) => ({
        index,
        timestamp: new Date(candle.timestamp),
        time: new Date(candle.timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        }),
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
        volume: candle.volume,
        // For line chart compatibility
        price: candle.close
      }));

      setChartData(formattedData);

    } catch (err) {
      console.error('Error fetching chart data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch detailed stock information
  const fetchStockInfo = async () => {
    if (!stockId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/stocks/${stockId}`);
      if (response.ok) {
        const data = await response.json();
        setStockInfo(data);
      }
    } catch (err) {
      console.error('Error fetching stock info:', err);
    }
  };

  // Fetch user account balance
  const fetchUserBalance = async () => {
    if (!userId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/${userId}`);
      if (response.ok) {
        const data = await response.json();
        const positionsValue = (data.positions || []).reduce((sum, p) => sum + (p.currentValue || 0), 0);
        setUserBalance({
          balance: data.balance || 0,
          totalValue: (data.balance || 0) + positionsValue
        });
      }
    } catch (err) {
      console.error('Error fetching user balance:', err);
    }
  };

  // Initialize data on component mount and when stockId changes
  useEffect(() => {
    fetchChartData();
    fetchStockInfo();
    fetchUserBalance();
  }, [stockId, userId]);

  // Refresh data when time range changes
  useEffect(() => {
    fetchChartData(timeRange);
  }, [timeRange]);

  // Auto-refresh chart data every minute to show new candlesticks
  useEffect(() => {
    // Refresh every 60 seconds for 1-minute candlesticks
    const refreshInterval = setInterval(() => {
      fetchChartData(timeRange, true); // Silent refresh - no loading spinner
    }, 60000); // 60 seconds

    return () => clearInterval(refreshInterval);
  }, [stockId, timeRange]);

  // Live price updates — patch the last candle with the latest price from socket
  useEffect(() => {
    if (!stockId && !ticker) return;

    const handleLivePrice = (updates) => {
      const stocksArray = Array.isArray(updates) ? updates : updates?.stocks;
      if (!stocksArray) return;

      const match = stocksArray.find(s =>
        s.id === stockId ||
        s.ticker === ticker ||
        s.ticker?.toUpperCase() === ticker?.toUpperCase()
      );
      if (!match) return;

      const livePrice = parseFloat(match.currentPrice);
      if (!livePrice || isNaN(livePrice)) return;

      setChartData(prev => {
        if (!prev || prev.length === 0) return prev;

        const updated = [...prev];
        const last = { ...updated[updated.length - 1] };

        // Update the last candle's close/high/low with the live price
        last.close = livePrice;
        last.price = livePrice; // for line/area charts
        last.high = Math.max(last.high, livePrice);
        last.low = Math.min(last.low, livePrice);

        updated[updated.length - 1] = last;
        return updated;
      });

      // Also update stockInfo price + percentage display
      setStockInfo(prev => {
        if (!prev) return prev;
        const basePrice = prev.basePrice || prev.currentPrice;
        const changePercent24h = basePrice > 0 ? ((livePrice - basePrice) / basePrice) * 100 : 0;
        return {
          ...prev,
          currentPrice: livePrice,
          changePercent24h: parseFloat(changePercent24h.toFixed(2)),
        };
      });
    };

    socket.on('stock-updates', handleLivePrice);
    return () => socket.off('stock-updates', handleLivePrice);
  }, [stockId, ticker]);

  // Handle time range changes
  const handleRangeChange = (newRange) => {
    setTimeRange(newRange);
  };

  // Handle chart type changes - persist to sessionStorage
  const handleChartTypeChange = (type) => {
    setChartType(type);
    sessionStorage.setItem(`chartType-${stockId}`, type);
  };

  // Trading functions
  const handleTrade = (type) => {
    if (!userId) {
      console.error('No user ID provided for trade execution');
      if (onPositionUpdate) onPositionUpdate({ error: 'User ID not provided' }, { actionType: `OPEN_${type}`, stockTicker: ticker });
      return;
    }

    const currentPrice = chartData.length > 0 ? chartData[chartData.length - 1].close : null;
    if (!currentPrice) {
      if (onPositionUpdate) onPositionUpdate({ error: 'Cannot determine current price' }, { actionType: `OPEN_${type}`, stockTicker: ticker });
      return;
    }

    // Refresh balance before showing modal
    fetchUserBalance();

    // Show confirmation modal
    const shares = Math.max(1, Math.min(100, parseInt(orderAmount) || 1));
    setOrderAmount(String(shares));
    setPendingTrade({
      type,
      shares,
      price: currentPrice,
      ticker
    });
    setShowConfirmModal(true);
  };

  const confirmTrade = async () => {
    if (!pendingTrade) return;

    setActionLoading(true);

    try {
      const result = await executeOrder(userId, stockId, pendingTrade.type, pendingTrade.shares);
      setShowConfirmModal(false);
      if (onPositionUpdate) {
        onPositionUpdate(
          result,
          {
            actionType: `OPEN_${pendingTrade.type}`,
            stockTicker: ticker,
            tradeAmount: pendingTrade.shares,
            entryPrice: result.position?.entryPrice || pendingTrade.price
          }
        );
      }
    } catch (error) {
      console.error('Trade failed:', error);
      setShowConfirmModal(false);
      if (onPositionUpdate) {
        onPositionUpdate(
          { error: error.message || 'Unknown error' },
          {
            actionType: `OPEN_${pendingTrade.type}`,
            stockTicker: ticker,
            tradeAmount: pendingTrade.shares,
            entryPrice: pendingTrade.price
          }
        );
      }
    } finally {
      setActionLoading(false);
      setPendingTrade(null);
    }
  };

  const cancelTrade = () => {
    setShowConfirmModal(false);
    setPendingTrade(null);
  };

  const handleClosePosition = async (positionId) => {
    if (!userId) {
      console.error('No user ID provided for closing position');
      return;
    }

    const positionToClose = stockPositions.find(p => p.id === positionId);
    if (!positionToClose) return;

    setActionLoading(true);
    try {
      const result = await closePosition(userId, positionId);
      if (onPositionUpdate) {
        onPositionUpdate(result, { actionType: 'CLOSE', stockTicker: ticker });
      }
    } catch (error) {
      console.error('Close position failed:', error);
      if (onPositionUpdate) {
        onPositionUpdate({ error: error.message || 'Unknown error' }, { actionType: 'CLOSE', stockTicker: ticker });
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Filter positions for this stock
  const stockPositions = positions.filter(position => position.stockId === stockId);

  // Calculate chart dimensions and domains
  const prices = chartData.map(d => [d.open, d.high, d.low, d.close]).flat().filter(p => p != null);
  const volumes = chartData.map(d => d.volume).filter(v => v != null);

  // Calculate price range with minimum 5% spread to prevent oversized candles
  let minPrice = prices.length > 0 ? Math.min(...prices) : 0;
  let maxPrice = prices.length > 0 ? Math.max(...prices) : 100;
  const avgPrice = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 50;
  const minRange = avgPrice * 0.05; // At least 5% range
  const currentRange = maxPrice - minPrice;

  if (currentRange < minRange) {
    const padding = (minRange - currentRange) / 2;
    minPrice = minPrice - padding;
    maxPrice = maxPrice + padding;
  } else {
    // Add small buffer
    minPrice = minPrice * 0.998;
    maxPrice = maxPrice * 1.002;
  }

  const maxVolume = volumes.length > 0 ? Math.max(...volumes) : 1000;

  // Custom tooltip for candlestick data
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{`Time: ${label}`}</p>
          <p className="tooltip-open">{`Open: ${formatCurrency(data.open)}`}</p>
          <p className="tooltip-high">{`High: ${formatCurrency(data.high)}`}</p>
          <p className="tooltip-low">{`Low: ${formatCurrency(data.low)}`}</p>
          <p className="tooltip-close">{`Close: ${formatCurrency(data.close)}`}</p>
          <p className="tooltip-volume">{`Volume: ${data.volume?.toLocaleString()}`}</p>
        </div>
      );
    }
    return null;
  };

  // Prepare volume chart data
  const volumeData = chartData.map(d => ({
    time: d.time,
    volume: d.volume,
    // Color volume bars based on price change
    color: d.close >= d.open ? '#00ff88' : '#ff4444'
  }));

  if (loading) {
    return (
      <div className="candlestick-chart-container">
        <div className="chart-loading">
          <div className="chart-loading-spinner"></div>
          <p>Loading chart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="candlestick-chart-container">
        <div className="error-message">Error: {error}</div>
        <button onClick={() => fetchChartData()} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="candlestick-chart-container">
      {/* Chart Header */}
      <div className="chart-header">
        <div className="chart-title">
          <h3>{ticker}{stockInfo?.name ? ` - ${stockInfo.name}` : ''}</h3>
          {stockInfo && (
            <div className="price-info">
              <span className="current-price">{formatCurrency(stockInfo.currentPrice)}</span>
              <span className={`price-change ${stockInfo.changePercent24h >= 0 ? 'positive' : 'negative'}`}>
                {stockInfo.changePercent24h >= 0 ? '+' : ''}
                {stockInfo.changePercent24h?.toFixed(2)}%
              </span>
            </div>
          )}
        </div>

        <div className="chart-controls">
          {/* Chart Type Selector */}
          <div className="chart-type-selector">
            {[
              { type: 'candlestick', emoji: '🕯️' },
              { type: 'line', emoji: '📈' },
              { type: 'area', emoji: '🏔️' },
              { type: 'bar', emoji: '📊' },
            ].map(({ type, emoji }) => (
              <button
                key={type}
                className={chartType === type ? 'active' : ''}
                onClick={() => handleChartTypeChange(type)}
                title={type}
              >
                {emoji}
              </button>
            ))}
          </div>

          {/* Time Range Selector */}
          <div className="time-range-selector">
            {['1m', '5m', '15m', '30m', '1h'].map(range => (
              <button
                key={range}
                className={timeRange === range ? 'active' : ''}
                onClick={() => handleRangeChange(range)}
              >
                {range.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Display Options */}
          <div className="display-options">
            <label>
              <input
                type="checkbox"
                checked={showVolume}
                onChange={(e) => setShowVolume(e.target.checked)}
              />
              Volume
            </label>
          </div>
        </div>
      </div>

      {/* Main Chart */}
      {chartData.length > 0 ? (
        <div className="chart-area">
          <div className="price-chart" style={{ height: isMobile ? (showVolume ? '140px' : '180px') : (showVolume ? '200px' : '280px') }}>
            <ResponsiveContainer width="100%" height="100%">
              {(() => {
                const commonProps = {
                  data: chartData,
                  margin: isMobile ? { top: 5, right: 10, bottom: 5, left: 0 } : { top: 10, right: 20, bottom: 5, left: 0 }
                };

                const commonElements = (
                  <>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: isMobile ? 9 : 12 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      domain={[minPrice, maxPrice]}
                      tickFormatter={value => isMobile ? (value >= 1000 ? `$${(value/1000).toFixed(1)}k` : `$${value.toFixed(2)}`) : formatCurrency(value)}
                      tick={{ fontSize: isMobile ? 9 : 12 }}
                      orientation="right"
                      width={isMobile ? 45 : 60}
                    />
                    {/* Position reference lines */}
                    {stockPositions.map((position, index) => (
                      <ReferenceLine
                        key={position.id || index}
                        y={position.entryPrice}
                        stroke={position.type === 'LONG' ? '#00ff88' : '#ff4444'}
                        strokeDasharray="3 3"
                        label={{
                          value: `${position.type} @ ${formatCurrency(position.entryPrice)}`,
                          fill: position.type === 'LONG' ? '#00ff88' : '#ff4444',
                          position: 'insideTopRight'
                        }}
                      />
                    ))}
                  </>
                );

                switch (chartType) {
                  case 'candlestick':
                    return (
                      <ComposedChart {...commonProps}>
                        {commonElements}
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                          dataKey="high"
                          shape={createCandlestickShape(minPrice, maxPrice)}
                          fill="transparent"
                        />
                      </ComposedChart>
                    );

                  case 'line':
                    return (
                      <LineChart {...commonProps}>
                        {commonElements}
                        <Tooltip
                          formatter={value => [formatCurrency(value), 'Price']}
                          labelFormatter={label => `Time: ${label}`}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="close"
                          stroke="#00bcd4"
                          dot={false}
                          activeDot={{ r: 6 }}
                          name={`${ticker} Price`}
                          strokeWidth={2}
                        />
                      </LineChart>
                    );

                  case 'area':
                    return (
                      <AreaChart {...commonProps}>
                        {commonElements}
                        <Tooltip
                          formatter={value => [formatCurrency(value), 'Price']}
                          labelFormatter={label => `Time: ${label}`}
                        />
                        <Legend />
                        <defs>
                          <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#00bcd4" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#00bcd4" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <Area
                          type="monotone"
                          dataKey="close"
                          stroke="#00bcd4"
                          fillOpacity={1}
                          fill="url(#colorPrice)"
                          strokeWidth={2}
                          name={`${ticker} Price`}
                        />
                      </AreaChart>
                    );

                  case 'bar':
                    return (
                      <BarChart {...commonProps}>
                        {commonElements}
                        <Tooltip
                          formatter={value => [formatCurrency(value), 'Close Price']}
                          labelFormatter={label => `Time: ${label}`}
                        />
                        <Legend />
                        <Bar
                          dataKey="close"
                          name={`${ticker} Close Price`}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={index > 0 && entry.close >= chartData[index - 1].close ? '#00ff88' : '#ff4444'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    );

                  default:
                    return null;
                }
              })()}
            </ResponsiveContainer>
          </div>

          {/* Volume Chart */}
          {showVolume && (
            <div className="volume-chart" style={{ height: isMobile ? '60px' : '80px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={volumeData} margin={{ top: 2, right: 15, bottom: 2, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: isMobile ? 8 : 10 }}
                    interval="preserveStartEnd"
                    hide={isMobile}
                  />
                  <YAxis
                    domain={[0, maxVolume]}
                    tick={{ fontSize: isMobile ? 8 : 10 }}
                    orientation="right"
                    width={isMobile ? 30 : 40}
                  />
                  <Tooltip
                    formatter={value => [value?.toLocaleString(), 'Volume']}
                    labelFormatter={label => `Time: ${label}`}
                  />

                  <Bar dataKey="volume">
                    {volumeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      ) : (
        <div className="no-chart-data">
          <p>No chart data available for {ticker}</p>
          <button onClick={() => fetchChartData()} className="retry-button">
            Refresh
          </button>
        </div>
      )}

      {/* Current Position Display - Above Trading */}
      {userId && stockPositions.length > 0 && (
        <div className="current-position-display">
          <div className="current-position-header">Your Current Position{stockPositions.length > 1 ? 's' : ''}:</div>
          {stockPositions.map((position, index) => {
            const currentPrice = chartData.length > 0 ? chartData[chartData.length - 1].close : 0;
            const profit = position.type === 'LONG'
              ? (currentPrice - position.entryPrice) * position.shares
              : (position.entryPrice - currentPrice) * position.shares;
            const profitPercent = position.entryPrice > 0
              ? ((profit / (position.entryPrice * position.shares)) * 100)
              : 0;

            return (
              <div
                key={position.id || index}
                className={`current-position-card ${position.type.toLowerCase()}`}
              >
                <div className="position-main-info">
                  <span className={`position-type-badge ${position.type.toLowerCase()}`}>
                    {position.type}
                  </span>
                  <span className="position-shares">
                    {position.shares} {position.shares === 1 ? 'share' : 'shares'}
                  </span>
                  <span className="position-entry">
                    @ {formatCurrency(position.entryPrice)}
                  </span>
                  <span className={`position-pnl ${profit >= 0 ? 'positive' : 'negative'}`}>
                    {profit >= 0 ? '+' : ''}{formatCurrency(profit)} ({profit >= 0 ? '+' : ''}{profitPercent.toFixed(1)}%)
                  </span>
                </div>
                <button
                  className="close-position-btn-inline"
                  onClick={() => handleClosePosition(position.id)}
                  disabled={actionLoading || !canTrade}
                >
                  {actionLoading ? 'Closing...' : 'Close'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Trading Actions */}
      {userId && (
        <div className="chart-trading-actions">
          <h4 className="trade-section-header">Trade {ticker}:</h4>
          <div className="order-amount-control">
            <label htmlFor="chart-order-amount">
              {isProMode ? 'QUANTITY:' : 'Amount:'}
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              id="chart-order-amount"
              value={orderAmount}
              onChange={(e) => setOrderAmount(e.target.value.replace(/[^0-9]/g, ''))}
              onBlur={() => setOrderAmount(prev => String(Math.max(1, Math.min(100, parseInt(prev) || 1))))}
            />
          </div>

          <div className="chart-trade-buttons">
            {!canTrade && (
              <div className="trading-disabled-notice">
                Trading is currently disabled
              </div>
            )}
            <button
              className="trade-btn long"
              onClick={() => handleTrade('LONG')}
              disabled={actionLoading || chartData.length === 0 || !canTrade}
            >
              {actionLoading ? 'Processing...' : `${isProMode ? 'LONG' : 'Long'} (${orderAmount})`}
            </button>
            <button
              className="trade-btn short"
              onClick={() => handleTrade('SHORT')}
              disabled={actionLoading || chartData.length === 0 || !canTrade}
            >
              {actionLoading ? 'Processing...' : `${isProMode ? 'SHORT' : 'Short'} (${orderAmount})`}
            </button>
          </div>
        </div>
      )}

      {/* Stock Statistics */}
      {stockInfo && (
        <div className="stock-statistics">
          <div className="stat-group">
            <div className="stat-item">
              <span className="stat-label">Start</span>
              <span className="stat-value">{formatCurrency(stockInfo.basePrice || 0)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">High</span>
              <span className="stat-value">{formatCurrency(stockInfo.stats.dayHigh || 0)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Low</span>
              <span className="stat-value">{formatCurrency(stockInfo.stats.dayLow || 0)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Volume</span>
              <span className="stat-value">{stockInfo.stats.totalVolume?.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Position Management */}
      {stockPositions.length > 0 && (
        <div className="position-info">
          <h4>Your Positions</h4>
          {stockPositions.map((position, index) => {
            const currentPrice = chartData.length > 0 ? chartData[chartData.length - 1].close : 0;
            const profit = position.type === 'LONG'
              ? (currentPrice - position.entryPrice) * position.shares
              : (position.entryPrice - currentPrice) * position.shares;

            return (
              <div
                key={position.id || index}
                className={`position-card mini ${position.type.toLowerCase()}`}
              >
                <div className="position-header">
                  <span className="type">{position.type}</span>
                  <span className="shares">{position.shares} {position.shares === 1 ? 'share' : 'shares'}</span>
                </div>
                <div className="position-details">
                  <div className="detail">
                    <span className="label">Entry:</span>
                    <span>{formatCurrency(position.entryPrice)}</span>
                  </div>
                  <div className="detail">
                    <span className="label">Current:</span>
                    <span>{formatCurrency(currentPrice)}</span>
                  </div>
                  <div className="detail">
                    <span className="label">P/L:</span>
                    <span className={profit >= 0 ? 'positive' : 'negative'}>
                      {profit >= 0 ? '+' : ''}
                      {formatCurrency(profit)}
                    </span>
                  </div>
                </div>

                {userId && (
                  <div className="position-actions">
                    <button
                      className="close-position-btn"
                      onClick={() => handleClosePosition(position.id)}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Closing...' : 'Close Position'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Trade Confirmation Modal */}
      {showConfirmModal && pendingTrade && (() => {
        // Mirror server slippage: 0.5% base + (shares/100)*0.5%, max 5%
        const baseSlippage = 0.005;
        const sizeSlippage = Math.min(0.045, (pendingTrade.shares / 100) * 0.005);
        const slippagePercent = baseSlippage + sizeSlippage;
        const execPrice = pendingTrade.type === 'LONG'
          ? pendingTrade.price * (1 + slippagePercent)
          : pendingTrade.price * (1 - slippagePercent);
        const totalCost = execPrice * pendingTrade.shares;
        const balanceAfter = userBalance ? userBalance.balance - totalCost : null;
        const insufficientFunds = balanceAfter !== null && balanceAfter < 0;

        return (
        <div className="trade-modal-overlay" onClick={cancelTrade}>
          <div className="trade-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isProMode ? 'CONFIRM TRADE' : 'Confirm Trade'}</h3>
              <button className="modal-close" onClick={cancelTrade} disabled={actionLoading}>×</button>
            </div>

            <div className="modal-body">
              <div className="trade-summary">
                <div className={`trade-type ${pendingTrade.type.toLowerCase()}`}>
                  {pendingTrade.type}
                </div>

                <div className="trade-detail">
                  <span className="label">{isProMode ? 'TICKER:' : 'Ticker:'}</span>
                  <span className="value">{pendingTrade.ticker}</span>
                </div>

                <div className="trade-detail">
                  <span className="label">{isProMode ? 'SHARES:' : 'Shares:'}</span>
                  <span className="value">{pendingTrade.shares}</span>
                </div>

                <div className="trade-detail">
                  <span className="label">{isProMode ? 'MARKET PRICE:' : 'Market Price:'}</span>
                  <span className="value">{formatCurrency(pendingTrade.price)}</span>
                </div>

                <div className="trade-detail">
                  <span className="label">{isProMode ? 'EXEC PRICE:' : 'Exec Price:'}</span>
                  <span className="value">
                    {formatCurrency(execPrice)}
                    <span style={{ fontSize: '0.75em', color: '#888', marginLeft: '4px' }}>
                      ({(slippagePercent * 100).toFixed(1)}% txn fee)
                    </span>
                  </span>
                </div>

                <div className="trade-detail total">
                  <span className="label">{isProMode ? 'TOTAL COST:' : 'Total Cost:'}</span>
                  <span className="value">{formatCurrency(totalCost)}</span>
                </div>
              </div>

              <div className="balance-info">
                {userBalance ? (
                  <>
                    <div className="balance-detail">
                      <span className="label">{isProMode ? 'CASH BALANCE:' : 'Cash Balance:'}</span>
                      <span className="value">{formatCurrency(userBalance.balance)}</span>
                    </div>

                    <div className="balance-detail remaining">
                      <span className="label">{isProMode ? 'AFTER TRADE:' : 'After Trade:'}</span>
                      <span className={`value ${insufficientFunds ? 'negative' : 'positive'}`}>
                        {formatCurrency(balanceAfter)}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="balance-detail">
                    <span className="label" style={{ color: '#888' }}>Loading balance...</span>
                  </div>
                )}
              </div>

              {insufficientFunds && (
                <div className="trade-error">
                  {isProMode ? 'INSUFFICIENT FUNDS!' : 'Insufficient funds!'}
                </div>
              )}

              <div className="trade-warning">
                {pendingTrade.type === 'LONG'
                  ? (isProMode
                      ? 'YOU WILL PROFIT IF THE PRICE GOES UP'
                      : 'You will profit if the price goes up')
                  : (isProMode
                      ? 'YOU WILL PROFIT IF THE PRICE GOES DOWN'
                      : 'You will profit if the price goes down')
                }
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={cancelTrade} disabled={actionLoading}>
                {isProMode ? 'CANCEL' : 'Cancel'}
              </button>
              <button
                className={`btn-confirm ${pendingTrade.type.toLowerCase()}`}
                onClick={confirmTrade}
                disabled={actionLoading || insufficientFunds}
              >
                {actionLoading ? (isProMode ? 'EXECUTING...' : 'Executing...') : (isProMode ? 'CONFIRM TRADE' : 'Confirm Trade')}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default CandlestickChart;